import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Flow, Link, ui, type FlowActionState, type FlowStep } from '../ui';
import { CREATOR_RATE_BPS } from '@goodbuild/rules/ledger';
import { formatMoney } from '@goodbuild/rules/format';
import { slugProblem, suggestSlug } from '@goodbuild/rules/slug';
import { ApiError, useApiMutation } from '../api';
import { Notice, Screen, useAvailability } from './shared';
import {
  NetEarnings,
  PRICE_CARDS,
  SteamEvidence,
  StoreAddress,
  useSteamFill,
  useStoreAddress,
  type SteamDraft,
} from './title-form';

/**
 * Adding a title on a studio's behalf, one question at a time (#127, #128).
 *
 * **This was six inputs in a panel on the games table.** It got there honestly:
 * #126 put a create back on the admin panel because self-serve registration is
 * shut and a studio that has never opened a portal has no other way to get its
 * first title. What it did not get was the form the studio's own submission had
 * — a Steam import, an address resolved against the catalogue while the name is
 * typed, and a worked example of what a price actually pays. So the door meant
 * for the studios least able to help themselves was the worse of the two, and
 * the person using it was somebody typing a game they did not make from notes,
 * which is exactly the case where guessing a slug or mistyping a price is most
 * likely and least visible.
 *
 * It asks the same questions in the same order as `/submit`, out of the same
 * `./title-form` parts, and posts to `POST /v1/admin/games` — which validates
 * with `titleCreateInput` and writes through `createTitle`, the portal's own.
 * There is still exactly one implementation of what a title is.
 *
 * **Bar one question, since #137: it does not ask for a gameplay link.** That
 * is the one place where matching `/submit` was matching the wrong thing — the
 * video is evidence for a decision, and the person filling this in is on the
 * deciding side of it. The step is gone rather than optional; see where it used
 * to be, at the foot of the steps below.
 *
 * ## Its own screen, not a panel
 *
 * A flow needs the width and it needs the visitor's whole attention, and
 * `/admin/games` is opened many times a day to look something up. A form that
 * takes the page over when it is open would put a modal in front of a table
 * somebody came to read; one that sits above the table is the first thing every
 * one of those visits scrolls past. So the table keeps a link and this is where
 * it goes.
 *
 * ## What it says that `/submit` does not
 *
 * **Which studio, first and always.** The studio question on `/submit` is
 * skipped for an account with one organisation, because there is no decision to
 * take. Here there is: staff can act for every studio on the platform, the
 * wrong one is an ordinary slip, and #126 added a *move it* control on the
 * games table precisely because that slip had happened. So it is question one
 * and it is never skipped, even in the case — a brand new deployment with a
 * single studio — where it has one answer.
 *
 * **That this is not the studio's submission.** A title created here is a
 * draft, exactly as a studio's own is, and curation is still a separate press
 * on the games table. The welcome screen says so, because somebody adding a
 * title for a studio that has already signed will reasonably expect the two
 * acts to be one and they are not.
 */
export function AdminNewTitleRoute(): React.ReactElement {
  /*
    No query, and there used to be one.

    It read `GET /v1/admin/studios` to fill the picker on the first step, and
    the screen refused outright when the list came back empty: a title had to
    belong to an organisation and the form could not invent one. There are no
    studios (#144), so the byline is a field with a default — nothing to fetch
    and nothing to be missing.
  */
  /*
    `query` is a settled pair rather than a request (#144).

    `Screen` takes one so it can hold the aside back and draw a loading state,
    and this page has nothing to wait for any more — the studio list it used to
    fetch is gone. Stated as an object rather than made optional on `Screen`,
    because every other caller genuinely has a query and an optional prop there
    is one somebody forgets to pass.
  */
  return (
    <Screen title="Add a title" aside="Catalogue" query={{ isPending: false, error: null }}>
      <NewTitleFlow />
    </Screen>
  );
}

/**
 * What happened, once the form has been sent.
 *
 * One outcome rather than the two `/submit` has. A studio's flow can create a
 * title and fail to submit it, because `mayPublish` gates the second call on
 * paperwork; nothing here submits anything — an administrator approves from the
 * games table, which is a different screen and a deliberate second act.
 */
interface Created {
  name: string;
  slug: string;
  developerName: string;
  /**
   * What the Steam art import managed, or null when there was no link to try.
   *
   * Reported rather than swallowed (#130). Most titles do not have all eight
   * slots on Valve's CDN, so "fetched six of eight" is the ordinary outcome and
   * has to read as success; and the ones that did not arrive are exactly what
   * somebody has to go and upload by hand (#133), so a receipt that said
   * nothing would leave a store page half-drawn with no clue why.
   */
  art: ArtImport | null;
}

interface ArtImport {
  imported: string[];
  absent: string[];
  rejected: { kind: string; message: string }[];
  ok: string;
  /** Set when the import itself could not be run. The title still exists. */
  failed?: string;
}

function NewTitleFlow(): React.ReactElement {
  const call = useApiMutation();
  const cache = useQueryClient();
  const [created, setCreated] = useState<Created | null>(null);
  const [found, setFound] = useState<SteamDraft | null>(null);

  const checkAvailability = useAvailability();
  const {
    address,
    resolve: resolveAddress,
    forget: forgetAddress,
  } = useStoreAddress(checkAvailability);
  const fillFromSteam = useSteamFill(setFound);

  const steps = useMemo(
    (): FlowStep[] => [
      /*
        The byline, which was a studio picker (#144).

        It asked *which organisation owns this title and receives the
        royalties*, from a list fetched off `GET /v1/admin/studios`, and it was
        the one question on this form that could not be answered wrong without
        misdirecting money. There is no organisation and no royalties: this is
        the string printed under the title on its store page.

        Still asked rather than assumed, and asked first. The Steam step below
        fills it from `draft.developers` when there is a link, which is the case
        where the answer is worth having and is not GOODBUILD — a title made
        with somebody, or under a label.
      */
      {
        kind: 'text',
        name: 'developerName',
        question: 'Who made it?',
        help: 'The name printed under the title on its store page. It can be corrected afterwards from the title’s own page.',
        placeholder: 'GOODBUILD',
        optional: true,
      },
      /*
        The Steam question, and it is a yes or a no rather than a box (#128).

        Same shape and same reasoning as `/submit`: opening with an optional
        link field makes the fast path look like work — a box to ignore before
        the real questions start — when answering yes is how somebody skips two
        screens. It matters more here, because the person filling this in is
        working from somebody else's game and the Steam page is very often the
        only source they have.
      */
      {
        kind: 'cards',
        name: 'onSteam',
        question: 'Does it have a Steam page?',
        help: 'If it does, we read it and fill in the name and the price.',
        options: [
          {
            value: 'yes',
            label: 'Yes',
            description: 'Paste the link and we fill in the name, the address and the price.',
          },
          {
            value: 'no',
            label: 'Not yet',
            description: 'No problem. It is three short questions either way.',
          },
        ],
      },
      {
        kind: 'text',
        name: 'steamLink',
        question: 'What is the link?',
        help: 'Next reads the page and fills in what we find. Nothing is created by this, and every answer stays editable.',
        placeholder: 'https://store.steampowered.com/app/730/',
        when: (values) => values['onSteam'] === 'yes',
        action: {
          busyLabel: 'Reading Steam',
          run: fillFromSteam,
        },
        /*
          What came back, under the field that fetched it.

          Read on the way back rather than on the way through — the flow moves
          the moment Steam answers — and that is the case it is for. Somebody
          adding a title for a studio they have not spoken to today is the most
          likely person in the product to paste the wrong app id, and a picture
          of the wrong game is the fastest way to find out.
        */
        preview: () => <SteamEvidence draft={found} />,
      },
      {
        kind: 'text',
        name: 'name',
        question: 'What is it called?',
        placeholder: 'Tiny City',
        maxLength: 120,
        autoCapitalize: 'words',
        validate: (value) => {
          if (value.length < 2) return 'Use at least 2 characters.';
          // The address is built from this, so a name with nothing to build one
          // from has to be caught here rather than at the create.
          if (slugProblem(suggestSlug(value))) {
            return 'We build the store address from the name, and this one has no letters or numbers in it.';
          }
          return null;
        },
        hint: (value) => {
          const typed = value.trim();
          const wanted = suggestSlug(typed);
          if (wanted === '') return null;

          // The resolved one when it belongs to this name, and the obvious one
          // while the lookup is still out — never a claim we have not checked.
          const resolved = address?.name === typed ? address.slug : wanted;
          return `goodbuild.cc/g/${resolved}`;
        },
        preview: (values) => (
          <StoreAddress
            name={values['name'] ?? ''}
            address={address}
            resolve={resolveAddress}
            held="It is this title's from the moment the draft exists, and renaming does not move it."
          />
        ),
        /*
          Resolved on the way out too, so the review screen always has one. The
          debounce under the field answers for anybody who pauses, which is most
          people; somebody who types a name and presses Enter inside four
          hundred milliseconds would reach the review with nothing resolved.
        */
        action: {
          busyLabel: 'Checking the address',
          run: async (value) => {
            await resolveAddress(value.trim());
            return {};
          },
        },
      },
      {
        kind: 'cards',
        name: 'priceMinor',
        question: 'What should it cost?',
        help: `Every one of these is a price this store can sell at — the lowest is the floor payment processing leaves us. Creators earn ${CREATOR_RATE_BPS / 100}% of the net on sales they bring, on this title and every other, and that is not set here.`,
        options: PRICE_CARDS,
        /*
          Stays on the question, like the studio's own does: the table below is
          what the answer means, and jumping would show it and take it away in
          the same instant. Worth more here than there, because the person
          picking a rung is not the person who will live with it — a price
          chosen for somebody else's game deserves a look at what it pays them.
        */
        stay: true,
        preview: (values) => <NetEarnings priceMinor={Number(values['priceMinor'])} />,
        display: (value) => formatMoney('EUR', Number(value)),
      },
      /*
        There is no gameplay question here, and there was (#137).

        It asked "Where can we watch it?" and it was the last screen of this
        flow, in the name of the sameness this form was built for: `/submit`
        asks it, so this asked it too. That reasoning is right about every other
        question and wrong about this one, because this one is not a term — it
        is *evidence*, and the two doors are asking for evidence from opposite
        sides of the same decision.

        A studio sending it is asking somebody here to form a view of a game
        nobody here has played. An administrator typing a title in is the other
        end of that: the studio has already signed, the view is formed, and the
        person filling this in is usually the person who would read the answer
        in the curation queue. So the screen asked somebody to paste a link to
        themselves.

        The link is not gone from the product. `studioTitleCreateInput` still
        requires it of `/submit`, the queue still renders it, and the column has
        been nullable since it was added — see the schema in
        `apps/api/src/lib/studio-portal`, which carries the argument for why
        the requirement belongs to a door rather than to a title.
      */
    ],
    [fillFromSteam, found, address, resolveAddress],
  );

  /**
   * Create the draft.
   *
   * One call rather than the two `/submit` makes. A studio's flow creates and
   * then submits, and the pair can come apart on `mayPublish`; nothing here
   * submits. Curation is moved from the games table, which is the same two
   * presses a studio's title takes and is the whole of section 5.5 — a screen
   * where filling in a form put a game on the store is a screen that ships one
   * by accident.
   */
  async function send(_previous: FlowActionState, form: FormData): Promise<FlowActionState> {
    const read = (name: string) => String(form.get(name) ?? '').trim();

    const name = read('name');
    /*
      Blank means GOODBUILD, and the server agrees.

      `titleCreateInput.developerName` carries the same default, so an empty
      field and an absent key land on the same byline rather than on a
      validation error about a question that was marked optional.
    */
    const developerName = read('developerName') || 'GOODBUILD';

    // The address the form was showing, unless it belongs to a different name.
    // `resolve` short-circuits when it already has an answer for this one, so
    // the ordinary case costs nothing.
    const slug = (await resolveAddress(name)).slug;

    /**
     * Fetch the studio's art off Valve's CDN, once the title exists (#130).
     *
     * **A second call, and its failure is never the create's.** The title is
     * already made by the time this runs; a Valve CDN having a bad afternoon
     * must not turn a successful create into an error somebody retries — which
     * would collide with the slug they just took. So this catches everything
     * and reports what happened on the receipt, where the answer is "upload
     * them by hand" rather than "try again".
     *
     * **Not inside `POST /admin/games`**, for the same reason one level down.
     * That endpoint writes through `createTitle`, the portal's own, and eight
     * outbound fetches to a third party do not belong inside the one function
     * both doors into the catalogue share.
     *
     * Returns null when there is no link, which is the manual-upload path
     * (#133): the receipt then says so and points at the art panel.
     */
    async function importArt(productId: string, link: string): Promise<ArtImport | null> {
      if (link.trim() === '') return null;

      try {
        return await call<ArtImport>(`/admin/games/${productId}/art/steam`, {
          method: 'POST',
          body: JSON.stringify({ link: link.trim() }),
        });
      } catch (caught) {
        return {
          imported: [],
          absent: [],
          rejected: [],
          ok: '',
          failed:
            caught instanceof ApiError
              ? caught.message
              : 'We could not reach Steam for the art. Nothing else is affected.',
        };
      }
    }

    try {
      const created = await call<{ id: string; ok: string }>('/admin/games', {
        method: 'POST',
        body: JSON.stringify({
          developerName,
          name,
          slug,
          priceMinor: Number(read('priceMinor')),
          /*
            No `gameplayUrl` (#137). The key is absent rather than empty, which
            is what `titleCreateInput` is written for: an optional field with no
            key is stripped by `z.object` and `createTitle` writes null, where
            an empty string would be parsed as a URL and refused.
          */
        }),
      });

      // The table this came from, the studio's own screen, and the counts on
      // the overview are all out of date from this line on.
      await cache.invalidateQueries({ queryKey: ['admin', 'games'] });
      await cache.invalidateQueries({ queryKey: ['admin', 'overview'] });

      setCreated({
        name,
        slug,
        developerName,
        art: await importArt(created.id, read('onSteam') === 'yes' ? read('steamLink') : ''),
      });
      return { error: null, ok: null };
    } catch (caught) {
      /*
       * A 409 is the address going between the lookup and the insert.
       *
       * Narrow and not impossible, and the resolution in hand is known to be
       * wrong at that point. `resolve` short-circuits on a name it has already
       * answered for, so without this, pressing the button again would send the
       * same taken slug for ever.
       */
      if (caught instanceof ApiError && caught.status === 409) forgetAddress();

      return {
        error: caught instanceof ApiError ? caught.message : 'That did not work. Try again.',
        ok: null,
      };
    }
  }

  if (created) {
    return (
      <Receipt
        created={created}
        onAnother={() => {
          setCreated(null);
          setFound(null);
        }}
      />
    );
  }

  return (
    <Flow
      steps={steps}
      action={send}
      submitLabel="Create as a draft"
      reviewTitle="Before it exists"
      reviewHelp={
        address
          ? `Change anything here. Nothing exists until you press the button — and when you do, goodbuild.cc/g/${address.slug} belongs to this title.`
          : 'Change anything here. Nothing exists until you press the button.'
      }
      welcome={{
        eyebrow: 'Catalogue',
        title: 'Add a title for a studio',
        body: [
          'This creates a title the same way a studio creates its own — the same questions, the same endpoint, the same draft at the end. It exists because a studio that has never opened a portal cannot create its first one.',
          'It arrives as a draft and is not listed. Approving it is a separate press on the games table, and so is releasing it: nothing on this form can put a game on the store.',
          'If the game has a Steam page, have the link ready. We read it and fill in most of this.',
        ],
        startLabel: 'Start',
      }}
    />
  );
}

/**
 * What was made, and the two things somebody needs next.
 *
 * A flow's own done screen is one line, and there are three facts worth having
 * here: which studio holds it, where it lives, and that it is not listed. The
 * last is the one that gets assumed — the person who just filled this in for a
 * studio they are on a call with will believe the game is on the store unless
 * told otherwise.
 */
function Receipt({
  created,
  onAnother,
}: {
  created: Created;
  onAnother: () => void;
}): React.ReactElement {
  return (
    <div className={ui.panel ?? ''}>
      <div className={ui.stack ?? ''}>
        <p className="gb-eyebrow">Created as a draft</p>
        <h2 className={ui.sectionTitle ?? ''}>
          {created.name} {'—'} {created.developerName}
        </h2>

        <Notice>
          It is a draft on both axes and nobody can see it. Approve it from the games table when it
          is ready, and release it separately — the address{' '}
          <code>goodbuild.cc/g/{created.slug}</code> is this title&rsquo;s either way.
        </Notice>

        <ArtOutcome art={created.art} />

        <p className="gb-body">
          Its price is set and can be changed from the games table, along with other currencies and
          regions. A build is pushed with a build token, which is minted under Build tokens.
        </p>

        <div className={ui.actions ?? ''}>
          <Link href="/admin/games" className={ui.button ?? ''}>
            Open the games table
          </Link>
          <button type="button" className={ui.buttonSecondary ?? ''} onClick={onAnother}>
            Add another
          </button>
          <Link href={`/g/${created.slug}`} className={ui.buttonSecondary ?? ''}>
            See the address
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * What the Steam art import managed, in the words somebody needs next (#130).
 *
 * Three states and each leads somewhere different, which is the whole reason
 * this is not one sentence:
 *
 *   **No link.** There was no Steam page, so nothing was tried, and every slot
 *   is waiting for a file. That is #133's path and the receipt points at it.
 *
 *   **Some arrived.** The ordinary outcome. Most titles do not have all eight
 *   on Valve's CDN — a game with no library page has no library capsule, one
 *   published before Valve introduced the vertical capsule has none, plenty of
 *   studios never made a wordmark — so a count reading "six of eight" must read
 *   as success rather than as a partial failure, and the four that did not
 *   arrive are named so somebody knows what to go and upload.
 *
 *   **The import could not run.** Valve unreachable, or a link that was not a
 *   store page. The title exists either way, which is the thing to say first:
 *   somebody who reads "that did not work" retries the whole form and collides
 *   with the slug they have just taken.
 */
function ArtOutcome({ art }: { art: ArtImport | null }): React.ReactElement {
  if (art === null) {
    return (
      <p className="gb-body">
        There was no Steam page, so it has no store art yet. Upload it from the games table — eight
        slots, each the same size as the one Steam asks for, so the studio&rsquo;s existing files
        drop straight in.
      </p>
    );
  }

  if (art.failed) {
    return (
      <>
        <p className="gb-body">{art.failed}</p>
        <p className="gb-body">
          The title is made and nothing about it needs redoing. Run the art import again from the
          games table, or upload the files there.
        </p>
      </>
    );
  }

  return (
    <>
      <p className="gb-body">{art.ok}</p>
      {art.absent.length > 0 ? (
        <p className={ui.statLabel ?? ''}>
          Steam has no {art.absent.map((kind) => kind.replace(/_/g, ' ')).join(', ')} for this app,
          which is ordinary — plenty of games never had one. Upload them from the games table if the
          studio has them.
        </p>
      ) : null}
      {art.rejected.length > 0 ? (
        <ul className="gb-body">
          {art.rejected.map((problem) => (
            <li key={problem.kind}>
              {problem.kind.replace(/_/g, ' ')}: {problem.message}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
